'use client';

import Link from 'next/link';
import styles from './SectorBiography.module.scss';
import { gaCta } from '@/src/helpers/gtag';

export interface Profile {
  name: string;
  photo: string;
  role?: string;
  expertise?: string;
}

export interface SectorBiographyProps {
  eyebrow: string;
  title: string;
  photo?: string;
  photoAlt?: string;
  profiles?: Profile[];
  profilesFootnote?: string;
  paragraphs: string[];
  ctaHref: string;
  ctaLabel: string;
  variant?: 'default' | 'landing';
}

export function SectorBiography({
  eyebrow,
  title,
  photo,
  photoAlt,
  profiles,
  profilesFootnote,
  paragraphs,
  ctaHref,
  ctaLabel,
}: SectorBiographyProps) {
  const hasProfiles = profiles && profiles.length > 0;

  return (
    <section className={`${styles.section} ${styles.biographySection}`}>
      <div className={styles.biographyInner}>
        <div
          className={`${styles.biographyPortrait} ${styles.desktopPortrait} ${
            hasProfiles ? styles.hasProfiles : ''
          }`.trim()}
        >
          {hasProfiles ? (
            <div className={styles.profiles}>
              {profiles!.map((profile, index) => (
                <div key={profile.name} className={styles.profileCard}>
                  {profile.expertise && (
                    <span className={styles.expertiseBadge}>{profile.expertise}</span>
                  )}
                  <div className={styles.profileImageWrapper}>
                    <img
                      src={profile.photo}
                      alt={profile.name}
                      className={styles.profileImage}
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.profileInfo}>
                    <p className={styles.profileName}>{profile.name}</p>
                    {profile.role && <p className={styles.profileRole}>{profile.role}</p>}
                  </div>
                  {index === 0 && profiles!.length > 1 && (
                    <span className={styles.complementPlus} aria-hidden="true">
                      +
                    </span>
                  )}
                </div>
              ))}
              {profilesFootnote && <p className={styles.profilesFootnote}>{profilesFootnote}</p>}
            </div>
          ) : (
            photo && <img src={photo} alt={photoAlt || ''} className={styles.biographyImage} />
          )}
        </div>
        <div className={styles.biographyText}>
          <p className={styles.eyebrow}>{eyebrow}</p>
          <h2 className={styles.sectionTitleLeft}>{title}</h2>
          {paragraphs.map((paragraph, index) => (
            <p key={index} className={styles.biographyParagraph}>
              {paragraph}
            </p>
          ))}
          <Link
            href={ctaHref}
            className={styles.ctaPrimary}
            onClick={() => gaCta(ctaLabel, 'sector_biography_cta', ctaHref)}
          >
            {ctaLabel}
          </Link>
        </div>
      </div>
    </section>
  );
}
